import { useEffect, useRef, useState, useCallback } from 'react';

const PROBE_TIMEOUT = 5000;
const PROBE_INTERVAL = 15000;

async function verifyConnectivity(axiosInstance) {
  if (axiosInstance) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT);
      await axiosInstance.get('/health', { signal: controller.signal });
      clearTimeout(timer);
      return true;
    } catch (_) {
      /* fall through to fetch fallback */
    }
  }

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT);
    const res = await fetch(window.location.origin, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (res.status > 0) return true;
  } catch (_) {
    /* fall through */
  }

  try {
    return navigator.onLine;
  } catch (_) {
    return false;
  }
}

export default function useOnlineStatus(axiosInstance) {
  const [isOnline, setIsOnline] = useState(false);
  const probingRef = useRef(false);
  const mountedRef = useRef(true);

  const probe = useCallback(async () => {
    if (probingRef.current) return;
    probingRef.current = true;

    const result = await verifyConnectivity(axiosInstance);

    if (mountedRef.current) {
      setIsOnline(result);
    }

    probingRef.current = false;
  }, [axiosInstance]);

  useEffect(() => {
    mountedRef.current = true;

    const handleOnline = () => { probe(); };
    const handleOffline = () => { probe(); };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    probe();

    const interval = setInterval(probe, PROBE_INTERVAL);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [probe]);

  return isOnline;
}
